import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Sidebar from '../../components/Sidebar';
import HeaderGlobal from '../../components/HeaderGlobal';
import UnifiedSearchFilterBar from '../../components/UnifiedSearchFilterBar';
import StudentResults from './StudentResults';
import StudentDetail from './StudentDetail';
import styles from '../../styles/PreasignacionMateriasStyles/PreasignacionMaterias.module.css';
import LoaderOverlay from '../../components/LoaderOverlay';
import { getStudents, getStudent } from '../../api/students';
import { getCycles } from '../../api/ciclosEscolares';
import { guardarPreasignaciones, getPreasignacionesPorEstudiante, getMateriasSugeridas, anularPreasignacion, eliminarDeCardex } from '../../api/preasignaciones';

function PreasignacionMateriasScreen() {
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    cicloEscolar: ''
  });

  // Estados para datos del backend
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Estado para materias del plan de estudios
  const [materiasDelPlan, setMateriasDelPlan] = useState([]);
  // Materias sugeridas por el backend (recursadas / siguientes / opcionales)
  const [sugeridas, setSugeridas] = useState({ recursadas: [], siguientes: [], opcionales: [] });
  // Metadatos de la preasignación (tetramestre, ciclo actual, bloqueo por ciclo)
  const [preasignInfo, setPreasignInfo] = useState({ tetramestre: null, yaPreasignado: false, cicloActual: null, preasignacionHabilitada: true, fechaHabilitacion: null, cicloSiguiente: null });
  // Estado para materias seleccionadas
  const [materiasSeleccionadas, setMateriasSeleccionadas] = useState([]);
  // Estado para preasignaciones existentes
  const [preasignacionesExistentes, setPreasignacionesExistentes] = useState([]);
  // Estado para inscripción del estudiante
  const [inscripcionActual, setInscripcionActual] = useState(null);
  // Materias con registros en kardex que requieren eliminación manual desde la UI
  const [cardexConRegistros, setCardexConRegistros] = useState([]);

  // Configuración de filtros para UnifiedSearchFilterBar
  const filterConfig = useMemo(() => [
    {
      key: 'cicloEscolar',
      label: 'Ciclo Escolar',
      apiService: getCycles,
      setDefault: true,
      defaultOption: 'Todos los ciclos'
    }
  ], []);

  // Cargar estudiantes cuando cambian los filtros o búsqueda
  useEffect(() => {
    const loadStudents = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = {
          no_pagination: true
          // No filtrar por estatus para mostrar todos los estudiantes
        };
        if (searchTerm) {
          params.search = searchTerm;
        }
        if (filters.cicloEscolar) {
          params.id_ciclo_escolar = filters.cicloEscolar;
        }
        const response = await getStudents(params);
        // La API puede devolver un objeto con results o directamente un array
        const studentsData = response.results || response || [];
        
        // Mapear los datos del backend al formato esperado por el componente
        const mappedStudents = studentsData.map(student => ({
          id: student.id_estudiante || student.id,
          id_estudiante: student.id_estudiante || student.id,
          nombre: student.nombre_completo || student.nombre,
          matricula: student.matricula,
          campus: student.campus_nombre || student.id_campus?.nombre_campus || '',
          id_campus: student.id_campus_id || student.id_campus?.id_campus || student.id_campus,
          programa: student.programa_nombre || student.id_programa_actual?.nombre_programa || '',
          id_programa: student.id_programa_actual_id || student.id_programa_actual?.id_programa || student.id_programa_actual,
          cicloEscolar: student.ciclo_escolar_nombre || student.ciclo_ingreso?.nombre_ciclo || '',
          ciclo_ingreso_id: student.ciclo_ingreso_id || student.ciclo_ingreso?.id_ciclo_escolar || student.ciclo_ingreso,
          seccion: student.seccion_nombre || student.id_seccion?.nombre_seccion || '',
          id_seccion: student.id_seccion_id || student.id_seccion?.id_seccion || student.id_seccion,
          institucion: student.institucion_nombre || student.id_institucion?.nombre_institucion || '',
          id_institucion: student.id_institucion_id || student.id_institucion?.id_institucion || student.id_institucion,
          periodoActual: student.periodo_inicial || '',
          periodo_inicial: student.periodo_inicial,
          creditos: student.creditos || 0,
          // Estos se cargarán después al seleccionar el estudiante
          materiasAsignadas: [],
          materiasPendientes: [],
          materiasOpcionales: []
        }));
        
        setStudents(mappedStudents);
      } catch (err) {
        console.error('Error cargando estudiantes:', err);
        setError('Error al cargar estudiantes');
        setStudents([]);
      } finally {
        setLoading(false);
      }
    };
    
    loadStudents();
  }, [filters.cicloEscolar, searchTerm]);

  // Función para cargar preasignaciones existentes
  const cargarPreasignaciones = useCallback(async (estudianteId) => {
    if (!estudianteId) return [];
    
    try {
      const preasignaciones = await getPreasignacionesPorEstudiante(estudianteId);
      return preasignaciones || [];
    } catch (error) {
      console.error('Error cargando preasignaciones:', error);
      return [];
    }
  }, []);

  // Función para obtener la inscripción actual del estudiante
  const obtenerInscripcionActual = useCallback(async (estudianteId) => {
    if (!estudianteId) return null;
    
    try {
      // Intentar obtener el detalle del estudiante con inscripción
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE || 'http://localhost:8000/api'}/escolar/estudiantes/${estudianteId}/detalle-inscripcion/`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        return data.inscripcion_actual || null;
      }
    } catch (error) {
      console.error('Error obteniendo inscripción:', error);
    }
    return null;
  }, []);

  const handleStudentSelect = async (student) => {
    setLoadingDetail(true);
    setError(null);
    setSuccessMessage(null);
    
    console.log('[PreasignacionMaterias] Estudiante seleccionado inicial:', student);
    
    try {
      // Siempre cargar datos completos del estudiante desde el backend para asegurar persistencia
      let studentCompleto = { ...student };
      
      try {
        const detalleEstudiante = await getStudent(student.id);
        console.log('[PreasignacionMaterias] Detalle estudiante del backend:', detalleEstudiante);
        
        // Obtener el ID del programa correctamente
        const idPrograma = detalleEstudiante.id_programa_actual_id || 
                          detalleEstudiante.id_programa_actual?.id_programa ||
                          (typeof detalleEstudiante.id_programa_actual === 'number' ? detalleEstudiante.id_programa_actual : null) ||
                          student.id_programa;
        
        // Obtener el ID de la sección académica
        const idSeccion = detalleEstudiante.seccion_id ||
                         detalleEstudiante.id_seccion_id ||
                         detalleEstudiante.id_seccion?.id_seccion ||
                         (typeof detalleEstudiante.id_seccion === 'number' ? detalleEstudiante.id_seccion : null) ||
                         student.id_seccion;
        
        console.log('[PreasignacionMaterias] ID Programa obtenido:', idPrograma);
        console.log('[PreasignacionMaterias] ID Sección obtenido:', idSeccion);
        
        studentCompleto = {
          ...student,
          id_programa: idPrograma,
          id_seccion: idSeccion,
          periodo_inicial: detalleEstudiante.periodo_inicial || student.periodo_inicial || 1,
          periodoActual: detalleEstudiante.periodo_inicial || student.periodo_inicial || 1,
          seccion: detalleEstudiante.seccion_nombre || student.seccion || '',
          institucion: detalleEstudiante.institucion_nombre || student.institucion || '',
          programa: detalleEstudiante.programa_nombre || student.programa || '',
          campus: detalleEstudiante.campus_nombre || student.campus || '',
          cicloEscolar: detalleEstudiante.ciclo_escolar_nombre || student.cicloEscolar || ''
        };
      } catch (e) {
        console.warn('No se pudo cargar detalle del estudiante, usando datos del listado:', e);
        studentCompleto.periodo_inicial = student.periodo_inicial || 1;
        studentCompleto.periodoActual = student.periodo_inicial || 1;
      }
      
      console.log('[PreasignacionMaterias] id_programa a buscar materias:', studentCompleto.id_programa);
      console.log('[PreasignacionMaterias] id_seccion a buscar materias:', studentCompleto.id_seccion);
      console.log('[PreasignacionMaterias] periodo_inicial:', studentCompleto.periodo_inicial);

      // Cargar materias sugeridas (clasificadas por el backend):
      //   recursadas → Tabla 1 (automáticas), siguientes → Tabla 2 (automáticas),
      //   opcionales → Tabla 3 (selección manual con checkbox).
      const estudianteId = studentCompleto.id_estudiante || studentCompleto.id;
      const sugeridasResp = await getMateriasSugeridas(estudianteId);
      const recursadasArr = sugeridasResp.recursadas || [];
      const siguientesArr = sugeridasResp.siguientes || [];
      const opcionalesArr = sugeridasResp.opcionales || [];
      const meta = sugeridasResp.meta || {};

      console.log('[PreasignacionMaterias] recursadas:', recursadasArr.length,
        'siguientes:', siguientesArr.length, 'opcionales:', opcionalesArr.length,
        'tetramestre:', meta.tetramestre, 'ya_preasignado:', meta.ya_preasignado);

      // Mapear materia (backend) → formato del componente
      const mapUI = (m) => ({
        id: m.id,
        materia: m.nombre_materia || m.nombre,
        creditos: m.creditos,
        modalidad: m.tipo || 'Presencial',
        prerrequisitos: false,
      });

      // Cargar preasignaciones existentes
      const preasignaciones = await cargarPreasignaciones(estudianteId);

      // Actualizar estudiante con las tres tablas
      const studentConMaterias = {
        ...studentCompleto,
        materiasAsignadas: recursadasArr.map(mapUI),   // Tabla 1: Materias Recursadas
        materiasPendientes: siguientesArr.map(mapUI),  // Tabla 2: Materias Siguientes
        materiasOpcionales: opcionalesArr.map(mapUI),  // Tabla 3: Materias Opcionales
      };

      setSelectedStudent(studentConMaterias);
      setMateriasDelPlan([]);
      setSugeridas({ recursadas: recursadasArr, siguientes: siguientesArr, opcionales: opcionalesArr });
      setPreasignInfo({
        tetramestre: meta.tetramestre ?? null,
        yaPreasignado: !!meta.ya_preasignado,
        cicloActual: meta.ciclo_actual || null,
        preasignacionHabilitada: meta.preasignacion_habilitada !== false,
        fechaHabilitacion: meta.fecha_habilitacion || null,
        cicloSiguiente: meta.ciclo_siguiente || null,
      });
      setPreasignacionesExistentes(preasignaciones);
      setInscripcionActual(null); // No depender de inscripción para guardar

      // Selección inicial: recursadas + siguientes (automáticas, bloqueadas) +
      // opcionales que ya estuvieran preasignadas.
      const autoIds = [...recursadasArr, ...siguientesArr].map(m => Number(m.id));
      const opcionalesIds = new Set(opcionalesArr.map(m => Number(m.id)));
      const opcionalesYaPreasignadas = preasignaciones
        .map(p => Number(p.id_materia))
        .filter(id => opcionalesIds.has(id));
      setMateriasSeleccionadas([...new Set([...autoIds, ...opcionalesYaPreasignadas])]);
      
    } catch (err) {
      console.error('Error al seleccionar estudiante:', err);
      setError('Error al cargar datos del estudiante');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleBackToSearch = () => {
    setSelectedStudent(null);
    setMateriasDelPlan([]);
    setMateriasSeleccionadas([]);
    setPreasignacionesExistentes([]);
    setSugeridas({ recursadas: [], siguientes: [], opcionales: [] });
    setPreasignInfo({ tetramestre: null, yaPreasignado: false, cicloActual: null, preasignacionHabilitada: true, fechaHabilitacion: null, cicloSiguiente: null });
    setInscripcionActual(null);
    setError(null);
    setSuccessMessage(null);
    setCardexConRegistros([]);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // Manejar selección/deselección de materia
  const handleMateriaToggle = (materiaId) => {
    const idNum = Number(materiaId);
    setMateriasSeleccionadas(prev => {
      const prevNums = prev.map(id => Number(id));
      if (prevNums.includes(idNum)) {
        return prev.filter(id => Number(id) !== idNum);
      } else {
        return [...prev, idNum];
      }
    });
  };

  // Manejar selección de todas las materias de una sección
  const handleSelectAll = (materias, seleccionar = true) => {
    const ids = materias.map(m => Number(m.id));
    setMateriasSeleccionadas(prev => {
      const prevNums = prev.map(id => Number(id));
      if (seleccionar) {
        return [...new Set([...prevNums, ...ids])];
      } else {
        return prevNums.filter(id => !ids.includes(id));
      }
    });
  };

  // Manejadores para preasignación
  const handlePreasignar = async () => {
    // Automáticas = recursadas + siguientes (todas). Manuales = opcionales marcadas.
    const autoIds = [...sugeridas.recursadas, ...sugeridas.siguientes].map(m => Number(m.id));
    const opcionalesIds = new Set(sugeridas.opcionales.map(m => Number(m.id)));
    const manualIds = materiasSeleccionadas.map(Number).filter(id => opcionalesIds.has(id));
    const allIds = [...new Set([...autoIds, ...manualIds])];

    console.log('[PreasignacionMateriasScreen] handlePreasignar:', {
      autoIds, manualIds, allIds
    });

    if (!allIds || allIds.length === 0) {
      setError('No hay materias para preasignar.');
      return;
    }

    // Regla de negocio: máximo recomendado de 6 materias por tetramestre
    // (recursadas + siguientes). Si al agregar opcionales se supera ese límite,
    // no se bloquea: se pide confirmación y, si el usuario acepta, se continúa.
    let confirmarExceso = false;
    if (allIds.length > 6) {
      confirmarExceso = window.confirm(
        'La preasignación supera el máximo recomendado de 6 materias para este tetramestre. ¿Desea continuar con la preasignación?'
      );
      if (!confirmarExceso) {
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      // reemplazar: limpia las preasignaciones previas y reconstruye el conjunto.
      const resultado = await guardarPreasignaciones(
        null,
        allIds,
        'AUTOMATICA',
        selectedStudent?.id_estudiante || selectedStudent?.id,
        { reemplazar: true, materiasManual: manualIds, confirmarExceso }
      );

      console.log('[PreasignacionMateriasScreen] Resultado guardarPreasignaciones:', resultado);

      if (resultado.success) {
        const creados = resultado.creados?.length || 0;
        const errores = resultado.errores?.length || 0;

        let mensaje = `Se preasignaron ${creados} materias (${autoIds.length} automáticas, ${manualIds.length} opcionales).`;
        if (errores > 0) {
          mensaje += ` ${errores} materias tuvieron errores.`;
        }
        setSuccessMessage(mensaje.trim());

        // Recargar preasignaciones y bloquear hasta el siguiente ciclo
        const estId = selectedStudent.id_estudiante || selectedStudent.id;
        const nuevasPreasignaciones = await cargarPreasignaciones(estId);
        setPreasignacionesExistentes(nuevasPreasignaciones);
        setMateriasSeleccionadas(nuevasPreasignaciones.map(p => Number(p.id_materia)));
        setPreasignInfo(prev => ({ ...prev, yaPreasignado: true }));
      }
    } catch (err) {
      console.error('Error al preasignar materias:', err);
      setError('Error al guardar las preasignaciones: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelar = () => {
    setSelectedStudent(null);
    setMateriasDelPlan([]);
    setMateriasSeleccionadas([]);
    setPreasignacionesExistentes([]);
    setSugeridas({ recursadas: [], siguientes: [], opcionales: [] });
    setPreasignInfo({ tetramestre: null, yaPreasignado: false, cicloActual: null, preasignacionHabilitada: true, fechaHabilitacion: null, cicloSiguiente: null });
    setInscripcionActual(null);
    setError(null);
    setSuccessMessage(null);
    setCardexConRegistros([]);
  };

  const handleAnularPreasignacion = async () => {
    const confirmado = window.confirm(
      '¿Está seguro de que desea ANULAR la preasignación de este estudiante?\n\n' +
      '• Se eliminarán todas las preasignaciones del ciclo actual.\n' +
      '• El estudiante será retirado de los grupos donde fue asignado (si no hay calificaciones capturadas).\n' +
      '• Si ya existen calificaciones o registros en el historial académico, se le informará pero NO se eliminarán automáticamente.'
    );
    if (!confirmado) return;

    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const estId = selectedStudent?.id_estudiante || selectedStudent?.id;
      const resultado = await anularPreasignacion(estId);

      if (resultado.success) {
        let mensaje = resultado.message || 'Preasignación anulada correctamente.';
        if (resultado.grupos_con_notas > 0) {
          mensaje += ` ${resultado.grupos_con_notas} grupo(s) NO fueron desasignados porque ya tienen calificaciones capturadas.`;
        }
        if (resultado.advertencia_cardex && resultado.cardex_con_registros?.length > 0) {
          setCardexConRegistros(resultado.cardex_con_registros);
        }
        setSuccessMessage(mensaje);

        // Recargar materias sugeridas y resetear estado de preasignación
        const sugeridasResp = await getMateriasSugeridas(estId);
        const recursadasArr = sugeridasResp.recursadas || [];
        const siguientesArr = sugeridasResp.siguientes || [];
        const opcionalesArr = sugeridasResp.opcionales || [];
        const meta = sugeridasResp.meta || {};

        const mapUI = (m) => ({
          id: m.id,
          materia: m.nombre_materia || m.nombre,
          creditos: m.creditos,
          modalidad: m.tipo || 'Presencial',
          prerrequisitos: false,
        });

        setSelectedStudent(prev => ({
          ...prev,
          materiasAsignadas: recursadasArr.map(mapUI),
          materiasPendientes: siguientesArr.map(mapUI),
          materiasOpcionales: opcionalesArr.map(mapUI),
        }));
        setSugeridas({ recursadas: recursadasArr, siguientes: siguientesArr, opcionales: opcionalesArr });
        setPreasignInfo({
          tetramestre: meta.tetramestre ?? null,
          yaPreasignado: false,
          cicloActual: meta.ciclo_actual || null,
          preasignacionHabilitada: meta.preasignacion_habilitada !== false,
          fechaHabilitacion: meta.fecha_habilitacion || null,
          cicloSiguiente: meta.ciclo_siguiente || null,
        });
        setPreasignacionesExistentes([]);
        const autoIds = [...recursadasArr, ...siguientesArr].map(m => Number(m.id));
        setMateriasSeleccionadas(autoIds);
      }
    } catch (err) {
      console.error('Error al anular preasignación:', err);
      setError('Error al anular la preasignación: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEliminarDeCardex = async (idMateria, nombreMateria) => {
    const confirmado = window.confirm(
      `¿Eliminar "${nombreMateria}" del historial académico (kárdex) de este estudiante?\n\nEsta acción no se puede deshacer.`
    );
    if (!confirmado) return;

    setLoading(true);
    try {
      const estId = selectedStudent?.id_estudiante || selectedStudent?.id;
      const resultado = await eliminarDeCardex(estId, idMateria);
      if (resultado.success) {
        setCardexConRegistros(prev => prev.filter(c => c.id_materia !== idMateria));
        setSuccessMessage(`"${nombreMateria}" eliminada del historial académico (${resultado.eliminados} registro(s)).`);
      }
    } catch (err) {
      setError('Error al eliminar del kárdex: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar estudiantes por ciclo escolar seleccionado
  const filteredStudents = students.filter(s => {
    if (!filters.cicloEscolar || filters.cicloEscolar === 'Todos') return true;
    return String(s.ciclo_ingreso_id) === String(filters.cicloEscolar);
  });

  return (
    <div className={styles.layout}>
      <div className={styles.sidebarColumn}>
        <Sidebar />
      </div>
      
      <div className={styles.contentColumn}>
        <div className={styles.contentArea}>
          <div className={styles.contentWrapper}>
            <HeaderGlobal title="Preasignación de Materias" />

            {error && (
              <div className={styles.errorMessage}>
                {error}
              </div>
            )}
            
            {successMessage && (
              <div className={styles.successMessage}>
                {successMessage}
              </div>
            )}

            {cardexConRegistros.length > 0 && (
              <div className={styles.cardexWarningPanel}>
                <div className={styles.cardexWarningTitle}>
                  ⚠️ Las siguientes materias aún aparecen en el historial académico (kárdex). Elimínalas para que no aparezcan en la constancia del estudiante.
                </div>
                <ul className={styles.cardexWarningList}>
                  {cardexConRegistros.map(c => (
                    <li key={c.id_materia} className={styles.cardexWarningItem}>
                      <span className={styles.cardexWarningNombre}>{c.nombre_materia}</span>
                      {c.ciclo && <span className={styles.cardexWarningCiclo}>{c.ciclo}</span>}
                      {c.calificacion_final != null && (
                        <span className={styles.cardexWarningCal}>Cal: {c.calificacion_final}</span>
                      )}
                      <button
                        className={styles.cardexEliminarBtn}
                        onClick={() => handleEliminarDeCardex(c.id_materia, c.nombre_materia)}
                        disabled={loading}
                      >
                        Eliminar del Kárdex
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {!selectedStudent ? (
              <>
                {/* Unified Search Filter Bar */}
                <UnifiedSearchFilterBar
                  filterConfig={filterConfig}
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  searchTerm={searchTerm}
                  onSearch={handleSearch}
                  searchPlaceholder="Buscar Estudiante por Matrícula o Nombre"
                  searchButtonText="Buscar"
                />

                {/* LoaderOverlay para la lista de estudiantes */}
                <LoaderOverlay visible={loading} fullscreen={true} />

                {/* Student Results */}
                {!loading && (
                  <StudentResults 
                    students={filteredStudents}
                    onStudentSelect={handleStudentSelect}
                  />
                )}
              </>
            ) : loadingDetail ? (
              <div className={styles.loadingContainer}>
                <LoaderOverlay visible={true} label="Cargando datos del estudiante..." fullscreen={true} />
              </div>
            ) : (
              /* Student Detail */
              <StudentDetail 
                student={selectedStudent}
                onBack={handleBackToSearch}
                onPreasignar={handlePreasignar}
                onCancelar={handleCancelar}
                onAnular={handleAnularPreasignacion}
                materiasSeleccionadas={materiasSeleccionadas}
                onMateriaToggle={handleMateriaToggle}
                onSelectAll={handleSelectAll}
                preasignacionesExistentes={preasignacionesExistentes}
                inscripcionActual={inscripcionActual}
                tetramestre={preasignInfo.tetramestre}
                yaPreasignado={preasignInfo.yaPreasignado}
                cicloActual={preasignInfo.cicloActual}
                preasignacionHabilitada={preasignInfo.preasignacionHabilitada}
                fechaHabilitacion={preasignInfo.fechaHabilitacion}
                cicloSiguiente={preasignInfo.cicloSiguiente}
                loading={loading}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PreasignacionMateriasScreen;
